import { get } from '@ember/helper';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { restartableTask, timeout } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import or from 'ember-truth-helpers/helpers/or';
import BvSelectMulti from 'volta/components/bv-select-multi';
import BvSelect from 'volta/components/bv-select';
import orElse from 'volta/helpers/or-else';

import type StoreService from 'volta/services/store';

interface ITokenFilterArgs
  extends IFilterArgs<{
    options?: string[];
    searchFn?: (query: object, filterValues: TFilterValues) => Promise<string[]>;
  }> {
  options?: string[];
  warehouseIds?: string[] | string;
  renderInplace?: string;
  horizontalPosition?: 'left' | 'right' | 'auto';
  matchTriggerWidth?: boolean;
  label?: string;
  placeholder?: string;
  noMatchesMessage?: string;
  searchFn?: (query: object, filterValues: TFilterValues) => Promise<string[]>;
}

interface ITokenFilterSignature {
  Args: ITokenFilterArgs;
}

export default class FiltersTokenFilterComponent extends Component<ITokenFilterSignature> {
  <template>
    {{! @glint-nocheck: not typesafe yet }}
    {{#if @multiple}}
      <BvSelectMulti
        @label={{@label}}
        @onOpen={{perform this.searchTask undefined}}
        @search={{perform this.searchTask}}
        @isLoading={{this.searchTask.isRunning}}
        @options={{this.options}}
        @noMatchesMessage={{@noMatchesMessage}}
        @placeholder={{@placeholder}}
        @allowClear={{true}}
        @selected={{this.values}}
        @fill={{true}}
        @renderInPlace={{orElse @componentArgs.renderInplace @renderInplace}}
        @horizontalPosition={{or (or @componentArgs.horizontalPosition @horizontalPosition) 'auto'}}
        @matchTriggerWidth={{orElse
          (orElse @componentArgs.matchTriggerWidth @matchTriggerWidth)
          true
        }}
        @onChange={{this.valuesDidChange}}
        @dataName={{@name}}
        @helpText={{@helpText}}
        ...attributes
        as |token|
      >

        {{token}}
      </BvSelectMulti>
    {{else}}
      <BvSelect
        @fill={{true}}
        @label={{@label}}
        @onOpen={{perform this.searchTask undefined}}
        @search={{perform this.searchTask}}
        @options={{this.options}}
        @selected={{get this.values 0}}
        @searchEnabled={{true}}
        @renderInPlace={{orElse @componentArgs.renderInPlace @renderInPlace}}
        @horizontalPosition={{or @componentArgs.horizontalPosition @horizontalPosition}}
        @matchTriggerWidth={{orElse
          (orElse @componentArgs.matchTriggerWidth @matchTriggerWidth)
          true
        }}
        @allowClear={{@allowClear}}
        @onChange={{this.singleValueDidChange}}
        @dataName={{@name}}
        as |token|
      >
        {{token}}
      </BvSelect>
    {{/if}}
  </template>
  // Services
  // ~~~~~

  @service store!: StoreService;

  // Properties
  // ~~~~~

  @tracked options = this.args.componentArgs?.options ?? [];

  // Computed Properties
  // ~~~~~

  get values() {
    return this.args.values ?? [];
  }

  get searchFn() {
    const { componentArgs, searchFn } = this.args;
    return componentArgs?.searchFn ?? searchFn;
  }

  get warehouseIds(): string[] {
    const { warehouseIds, filterValues = {} } = this.args;
    if (warehouseIds && warehouseIds.length > 0) {
      return typeof warehouseIds === 'string' ? warehouseIds.split(',') : warehouseIds;
    } else {
      return (filterValues['warehouse']?.values as string[]) ?? [];
    }
  }

  // Lifecycle Hooks
  // ~~~~~

  // Actions
  // ~~~~~

  @action
  valuesDidChange(values: string[]) {
    return this.args.onChange?.(values);
  }

  @action
  singleValueDidChange(option?: string) {
    this.args.onChange?.(option ? [option] : []);
  }

  // Methods
  // ~~~~~
  searchTask = restartableTask(async (term?: string) => {
    await timeout(600);
    const { warehouseIds, searchFn } = this;
    const query: Record<string, any> = { search: term, pageSize: 100 };

    if (warehouseIds.length > 0) {
      query['warehouses'] = warehouseIds.join(',');
    }

    const items: string[] =
      typeof searchFn === 'function' ? await searchFn(query, this.args.filterValues) : [];
    this.options = items;
    return items;
  });
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Filters::TokenFilter': typeof FiltersTokenFilterComponent;
    'filters/token-filter': typeof FiltersTokenFilterComponent;
  }
}
